import React, { useState, useMemo } from 'react';
import MobileFilterDropdown from './MobileFilterDropdown'; // Ensure MobileFilterDropdown is imported
import { categoryIcons, colorClasses, badgeBaseClass, filterOrder } from '../utils/categoryStyles'; // Added filterOrder
import MobileFilterModal from './MobileFilterModal'; // Step 2: Import modal
import ActiveFilters from './ActiveFilters'; // Import ActiveFilters for modal summary
import { Filter as FilterIcon } from 'lucide-react'; // Use Lucide Filter icon
import { useOutletContext } from 'react-router-dom';

const MobileFilterBar = ({ filters, setFilters, allFilterOptions, allResources = [], resourceFieldMap, categoryIcons: propCategoryIcons }) => {
  const [openCategory, setOpenCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [pendingFilters, setPendingFilters] = useState(filters); // Step 5: Local state for modal changes

  // Get modal state from context
  const { setFilterModalOpen, filterModalOpen } = useOutletContext();

  // When modal opens, sync pendingFilters with real filters
  React.useEffect(() => {
    if (filterModalOpen) {
      setPendingFilters(filters);
    }
  }, [filterModalOpen, filters]);

  const filterConfig = useMemo(() => {
    const generateFilteredValues = (targetCategoryKey, usePending = false) => {
      const activeFilters = usePending ? pendingFilters : filters;
      const activeCategories = Object.keys(activeFilters).filter(catKey => activeFilters[catKey]?.length && catKey !== targetCategoryKey);
      
      if (activeCategories.length === 0 || !allResources || allResources.length === 0 || !resourceFieldMap) {
        return null; // No other active filters, or no resources to filter, or no field map
      }

      const targetResourceField = resourceFieldMap[targetCategoryKey];
      if (!targetResourceField) return null; // Target category not in map

      const filteredDownResources = allResources
      .filter(res => activeCategories.every(cat => {
        const field = resourceFieldMap[cat];
        if (!res[field]) return false;
        const match = activeFilters[cat].some(val => {
          const normalizedVal = val.toLowerCase().trim();
          const resValue = res[field];
          if (!resValue) return false;
          if (Array.isArray(resValue)) {
            return resValue.some(item => item?.toLowerCase?.().trim() === normalizedVal);
          } else {
            return String(resValue).toLowerCase().trim() === normalizedVal;
          }
        });
        return match;
      }))
      const uniqueValuesForTarget = Array.from(
        new Set(filteredDownResources.map(res => res[targetResourceField]).filter(Boolean).map(String))
      );
      return uniqueValuesForTarget.length > 0 ? uniqueValuesForTarget : null;
    };

     // Use propCategoryIcons if available, otherwise fallback to the imported one (though prop is preferred)
    const currentCategoryIcons = propCategoryIcons || categoryIcons;

    // Build filterConfig based on the preferred category order
    const config = {};
    filterOrder.categories.forEach(categoryKey => {
      const options = allFilterOptions?.[categoryKey] || [];
      if (options.length > 0) { // Only include categories that have options
        const categoryConfig = currentCategoryIcons[categoryKey];
        let headerLabel = categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
        if (categoryKey === 'digits') headerLabel = 'Numeral System';
        if (categoryKey === 'subtopic') headerLabel = 'Topic'; // Change "Subtopic" to "Topic"

        // Sort options within this category
        const order = filterOrder.options[categoryKey];
        const sortedOptions = order ? [...options].sort((a, b) => {
          const aLower = a.toLowerCase().replace(/\s+/g, categoryKey === 'type' ? '-' : '_');
          const bLower = b.toLowerCase().replace(/\s+/g, categoryKey === 'type' ? '-' : '_');
          const aIndex = order.indexOf(aLower);
          const bIndex = order.indexOf(bLower);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        }) : options; // Use original order if no specific order defined

        config[categoryKey] = {
          label: headerLabel,
          options: sortedOptions, // Use sorted options
          filteredOptions: generateFilteredValues(categoryKey, filterModalOpen), // Use pendingFilters in modal
          icon: categoryConfig?.icon,
          color: categoryConfig?.color || 'gray',
        };
      }
    });

    return config;
  }, [allFilterOptions, allResources, filters, pendingFilters, resourceFieldMap, propCategoryIcons, filterOrder, filterModalOpen]); // Added filterOrder to dependencies

  // Helper to get count of resources for a filter option (copied from FilterPanel)
  const getOptionCount = (category, option, filterState) => {
    const lowerOption = option.toLowerCase();
    return allResources.filter(res => {
      const field = resourceFieldMap[category];
      let resourceValue = res[field];
      if (category === 'digits') {
        resourceValue = resourceValue === 'English' ? 'English Numerals' :
                        resourceValue === 'Marathi' ? 'Devnagiri Numerals' :
                        resourceValue;
      }
      if (!resourceValue || resourceValue.trim().toLowerCase() !== lowerOption) {
        return false;
      }
      return Object.entries(filterState).every(([otherCatKey, otherCatVals]) => {
        if (otherCatKey === category || otherCatVals.length === 0) return true;
        const otherResourceField = resourceFieldMap[otherCatKey];
        let otherResourceValue = res[otherResourceField];
        if (otherCatKey === 'digits') {
          const transformedOtherResourceValue = (otherResourceValue === 'English' ? 'English Numerals' :
                                                 otherResourceValue === 'Marathi' ? 'Devnagiri Numerals' :
                                                 otherResourceValue)?.trim().toLowerCase();
          return otherCatVals.some(v => v === transformedOtherResourceValue);
        } else {
          return otherCatVals.some(v => v === otherResourceValue?.trim().toLowerCase());
        }
      });
    }).length;
  };

  // Conditional rendering if essential data for filters isn't ready
  if (!allFilterOptions || Object.keys(allFilterOptions).every(key => !(allFilterOptions[key]?.length > 0))) {
    // You might want to show a loading indicator or simply not render the filter bar
    // until allFilterOptions are populated.
    // For now, it will render with empty options if allFilterOptions is empty.
    // Or you could return null here if you prefer the bar not to show at all until options load.
    // return null;
  }

  // Handler for Apply
  const handleApply = () => {
    setFilters(pendingFilters);
    setFilterModalOpen(false);
  };
  // Handler for Cancel
  const handleCancel = () => {
    setFilterModalOpen(false);
    setPendingFilters(filters); // Reset pending changes
  };

  // Handler for opening modal: reset openCategory
  const handleOpenModal = () => {
    setOpenCategory(null);
    setFilterModalOpen(true);
  };

  return (
    <>
      {/* Filter button to open the modal */}
      <div className="flex items-center justify-between px-2 py-2 bg-white sticky top-0 z-40 lg:hidden">
        <button
          className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 bg-white rounded-full shadow hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onClick={handleOpenModal}
        >
          <FilterIcon className="w-5 h-5" />
          <span>Filter</span>
        </button>
      </div>
      {/* Modal with filter controls inside */}
      <MobileFilterModal
        open={filterModalOpen}
        onClose={handleCancel}
        onCancel={handleCancel}
        onApply={handleApply}
      >
        <div className="flex flex-col gap-2">
          {/* Sticky Pending Filters bar with Clear All */}
          <div className="sticky top-0 z-20 bg-gray-50 pt-1 pb-1">
            <div className="flex items-center justify-between mb-1 px-1">
              <span className="font-bold text-xs text-gray-700">Pending Filters</span>
              <button
                onClick={() => setPendingFilters({ subject: [], subtopic: [], type: [], difficulty: [], digits: [] })}
                className="text-xs text-blue-600 underline px-1 py-0.5 hover:text-blue-800 focus:outline-none"
                aria-label="Clear all pending filters"
              >
                Clear All
              </button>
            </div>
            <ActiveFilters activeFilters={pendingFilters} setFilters={setPendingFilters} title="" noMargin />
          </div>
          {filterConfig && Object.entries(filterConfig).map(([category, config], idx, arr) => {
            // Compute disabled options for this category
            const disabledOptions = config.options.filter(option => {
              const count = getOptionCount(category, option, pendingFilters);
              const isSelected = (pendingFilters[category] || []).includes(option.toLowerCase());
              return count === 0 && !isSelected;
            });
            return (
              <div key={category} className={idx !== arr.length - 1 ? 'border-b border-gray-100' : ''}>
                <MobileFilterDropdown
                  category={category}
                  label={config.label}
                  options={config.options}
                  icon={config.icon}
                  color={config.color}
                  openCategory={openCategory}
                  setOpenCategory={setOpenCategory}
                  filters={pendingFilters}
                  setFilters={setPendingFilters}
                  disabledOptions={disabledOptions}
                />
              </div>
            );
          })}
        </div>
      </MobileFilterModal>
    </>
  );
};

export default MobileFilterBar;
